'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { analyticsAddToCart } from '@/lib/analytics';
import { logger } from '@/lib/logger';
import { pixelTrackAddToCart } from '@/lib/pixel';
import { cartService } from '@/services';
import { IProduct } from '@/services/types';

import { type ProductVariant } from './ProductVariantSelector';

export type ProductAddToCartButtonProps = {
  product: IProduct & { id: string };
  selectedVariant?: ProductVariant | null;
  quantity?: number;
  className?: string;
  disabled?: boolean;
  outOfStock?: boolean;
};

const ProductAddToCartButton: React.FC<ProductAddToCartButtonProps> = ({ product, selectedVariant = null, quantity = 1, className = '', disabled = false, outOfStock = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  const handleAddToCart = async () => {
    if (!product.id) return;

    setIsLoading(true);
    setError(null);
    try {
      // Resolve the active sale from any of the three sources the API may use:
      // direct product.sale, enriched effective_sale, or the pivot array sales[0].sale.
      const effectiveSale = (product as any).sale ?? (product as any).effective_sale ?? (product as any).sales?.[0]?.sale ?? null;

      // Normalize the product so CartView can always read the sale from product.sale,
      // regardless of which field the API populated.
      const productWithSale = effectiveSale && !(product as any).sale ? { ...(product as any), sale: effectiveSale } : product;

      // Create order object
      const order = {
        product: productWithSale,
        pricetype: 'public',
        quantity: quantity,
        ...(selectedVariant && { variant: selectedVariant }),
        ...(effectiveSale && { cart_discounts: [{ id: effectiveSale.id }] }),
      };

      // Persist using CartService so the local key includes variant id when present
      await cartService.addToCart(order as any);

      // Report only the item being added (not the full cart) per Meta best practices.
      const addedPrice = (product as any).price_public_value ?? (product as any).price_retail_value ?? (product as any).price_wholesale_value ?? 0;
      pixelTrackAddToCart({
        contentIds: [String(product.id)],
        numItems: quantity,
        value: addedPrice * quantity,
      });

      analyticsAddToCart({
        contentIds: [String(product.id)],
        numItems: quantity,
        value: addedPrice * quantity,
      });

      logger.info({ msg: 'Producto agregado al carrito local', productId: product.id, quantity, variant: selectedVariant });

      // Redirect to cart page to show local data
      router.push('/cart');
    } catch (error) {
      logger.error({ msg: 'Error al agregar producto al carrito', error });
      if (error instanceof Error) {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={className}>
      {outOfStock ? (
        <div className="px-6 py-3 bg-gray-100 text-gray-500 font-semibold rounded-lg border-2 border-dashed border-gray-300 text-center w-full select-none">No hay stock</div>
      ) : (
        <button
          onClick={handleAddToCart}
          disabled={isLoading || disabled}
          data-testid="add-to-cart-button"
          className="px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full"
        >
          {isLoading ? 'Agregando...' : 'Agregar al Carrito'}
        </button>
      )}
      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default ProductAddToCartButton;
